import {
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import {
  Button,
  Flex,
  List,
  Popconfirm,
  Upload,
  Typography,
  Space,
} from 'antd';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps, RcFile } from 'antd/es/upload';
import type { Document } from './types/Document';
import { Link } from 'react-router-dom';

const { Dragger } = Upload;
const { Text } = Typography;

type UploadFileProps = {
  isMult?: boolean;
  mimeAccept?: string[];
  documents: Document[];
  fileList: UploadFile[];
  onRemove: (file: any) => void;
  onFileUpload: (file: RcFile) => Promise<void>;
};

const isImage = (url: string) => /\.(jpg|jpeg|png|gif|bmp)$/i.test(url);

const UploadFile = ({
  isMult = false,
  mimeAccept,
  documents,
  fileList,
  onRemove,
  onFileUpload,
}: UploadFileProps) => {
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: isMult,
    accept: mimeAccept?.join(','),
    fileList,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      try {
        await onFileUpload(file as RcFile);
        onSuccess?.('ok');
      } catch (err) {
        onError?.(err as Error);
      }
    },
    onChange(info) {},
  };

  const getFileIconByExtension = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    console.log(ext);

    switch (ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return <FileImageOutlined style={{ fontSize: 32, color: '#69c0ff' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ fontSize: 32, color: '#ff7875' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ fontSize: 32, color: '#2f54eb' }} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined style={{ fontSize: 32, color: '#52c41a' }} />;
      case 'txt':
      case 'md':
        return <FileTextOutlined style={{ fontSize: 32, color: '#8c8c8c' }} />;
      default:
        return (
          <FileUnknownOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
        );
    }
  };

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Dragger {...uploadProps} showUploadList={false}>
          {documents.length == 0 ? (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for a single or bulk upload. Do not upload confidential
                data.
              </p>
            </>
          ) : (
            <Flex align="center" gap={'middle'}>
              <InboxOutlined />
              <Text>
                Upload more files by dragging here or clicking the area
              </Text>
            </Flex>
          )}
        </Dragger>
        {documents.length > 0 && (
          <List
            bordered
            dataSource={documents}
            renderItem={(file) => (
              <List.Item
                itemType="picture"
                actions={[
                  <Button
                    type="text"
                    key={'download'}
                    onClick={() => window.open(file.url, '_blank')}
                    download
                  >
                    <DownloadOutlined color={'gray'} />
                  </Button>,
                  <Popconfirm
                    key={'remove'}
                    title="Delete the document"
                    description="Are you sure to delete this document?"
                    onConfirm={(e) => onRemove(file)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text">
                      <DeleteOutlined color={'gray'} />
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    isImage(file.url) ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      getFileIconByExtension(file.url)
                    )
                  }
                  title={file.name}
                />
              </List.Item>
            )}
          />
        )}
      </Space>
    </>
  );
};

export default UploadFile;
